package app.smartcoding.realrate.presentation.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Description
import androidx.compose.material.icons.filled.Policy
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.domain.model.AuthUser
import app.smartcoding.realrate.domain.model.LanguageCode
import app.smartcoding.realrate.domain.model.ThemeOption
import app.smartcoding.realrate.localization.LocalStrings

@Composable
fun AccountScreen(
    currentUser: AuthUser,
    onSignIn: () -> Unit,
    onSignOut: () -> Unit,
    onDismiss: () -> Unit,
    currentTheme: ThemeOption,
    onThemeSelected: suspend (ThemeOption) -> Unit,
    currentLanguageCode: LanguageCode,
    onLanguageCodeSelected: suspend (LanguageCode) -> Unit
) {
    val uriHandler = LocalUriHandler.current
    val strings = LocalStrings.current

    LazyColumn(
        modifier = Modifier.padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        item {
            Box(modifier = Modifier.fillMaxWidth()) {
                if (currentUser is AuthUser.User) {
                    Text(
                        text = currentUser.email ?: "",
                        modifier = Modifier.align(Alignment.Center),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                IconButton(
                    onClick = onDismiss,
                    modifier = Modifier.align(Alignment.CenterEnd)
                ) {
                    Icon(Icons.Default.Close, contentDescription = strings.close)
                }
            }
            Spacer(modifier = Modifier.height(16.dp))
        }

        item {
            UserAvatar(currentUser)
            Spacer(modifier = Modifier.height(8.dp))
            val greetingName = if (currentUser is AuthUser.User) currentUser.displayName ?: strings.guest else strings.guest
            Text(
                text = "${strings.hi}, $greetingName!",
                style = MaterialTheme.typography.headlineSmall
            )
            Spacer(modifier = Modifier.height(24.dp))
        }

        item {
            SectionTitle(strings.settings)
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.large,
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainer)
            ) {
                Column {
                    ExpandableThemeSetting(
                        currentTheme = currentTheme,
                        onThemeSelected = onThemeSelected
                    )

                    HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))

                    ExpandableLanguageSetting(
                        currentLanguageCode = currentLanguageCode,
                        onLanguageSelected = onLanguageCodeSelected
                    )
                }
            }
            Spacer(modifier = Modifier.height(24.dp))
        }

        item {
            SectionTitle(strings.account)
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.large,
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainer)
            ) {
                if (currentUser is AuthUser.Unknown) {
                    ActionRow(
                        icon = Icons.Default.Add,
                        text = strings.signInWithGoogle,
                        onClick = onSignIn
                    )
                } else {
                    ActionRow(
                        icon = Icons.AutoMirrored.Filled.Logout,
                        text = strings.signOut,
                        onClick = onSignOut
                    )
                }
            }
            Spacer(modifier = Modifier.height(24.dp))
        }

        item {
            SectionTitle(strings.about)
            Card(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.large,
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainer)
            ) {
                Column {
                    ActionRow(
                        icon = Icons.Default.Policy,
                        text = strings.privacyPolicy,
                        onClick = { uriHandler.openUri("https://checkrealrate.com/privacy.html") }
                    )
                    HorizontalDivider(modifier = Modifier.padding(horizontal = 16.dp))
                    ActionRow(
                        icon = Icons.Default.Description,
                        text = strings.termsOfService,
                        onClick = { uriHandler.openUri("https://checkrealrate.com/terms.html") }
                    )
                }
            }
            Spacer(modifier = Modifier.height(32.dp))
        }
    }
}

@Composable
private fun SectionTitle(title: String) {
    Text(
        text = title,
        style = MaterialTheme.typography.labelLarge,
        color = MaterialTheme.colorScheme.primary,
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 8.dp)
    )
}

@Composable
private fun ActionRow(
    icon: ImageVector,
    text: String,
    onClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() }
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(24.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = text,
            style = MaterialTheme.typography.bodyLarge,
            fontWeight = FontWeight.Medium
        )
    }
}
